import { apiAuth, apiResponseType } from ".";

export const getStocksData = (page: number = 1): apiResponseType =>
  apiAuth.get(`stocks?page=${page}`);

export const getStocksPerUnities = (unit_id?: number): apiResponseType =>
  apiAuth.get(`stocks/unities/?${unit_id ? `unit_id=${unit_id}` : ""}`);

export const getStocksPerSellers = (unit_id?: number): apiResponseType =>
  apiAuth.get(`stocks/sellers/?${unit_id ? `unit_id=${unit_id}` : ""}`);

// Novos endpoints para a página de estoques refatorada
export const getStocksByContest = (contest_id: number, page: number = 1, product_name?: string): apiResponseType => {
  const params = new URLSearchParams({
    contest_id: contest_id.toString(),
    page: page.toString(),
  });
  if (product_name) params.append("product_name", product_name);
  return apiAuth.get(`stocks/?${params.toString()}`);
};

export const getStocksDetailed = (
  contest_id: number,
  unity_id?: number,
  cp_classification_id?: number
): apiResponseType => {
  const params = new URLSearchParams({ contest_id: contest_id.toString() });
  if (unity_id) params.append("unity_id", unity_id.toString());
  if (cp_classification_id) params.append("cp_classification_id", cp_classification_id.toString());
  return apiAuth.get(`stocks/detailed?${params.toString()}`);
};

export interface StockSellersSummaryFilters {
  seller_id?: number[];
  lottery_id?: number[];
  contest_id?: number;
  start_date?: string;
  end_date?: string;
}

export const getStocksSellersSummary = (
  page: number = 1,
  filters?: StockSellersSummaryFilters
): apiResponseType => {
  const params = new URLSearchParams({ page: page.toString() });

  if (filters?.seller_id && filters.seller_id.length > 0) {
    filters.seller_id.forEach(id => params.append("seller_id[]", id.toString()));
  }
  if (filters?.lottery_id && filters.lottery_id.length > 0) {
    filters.lottery_id.forEach(id => params.append("lottery_id[]", id.toString()));
  }
  if (filters?.contest_id) params.append("contest_id", filters.contest_id.toString());
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);

  return apiAuth.get(`stocks/sellers-summary?${params.toString()}`);
};

export const getStocksSellersSummaryDetails = (
  contestId: number,
  filters?: StockSellersSummaryFilters
): apiResponseType => {
  const params = new URLSearchParams({ contest_id: contestId.toString() });

  if (filters?.seller_id && filters.seller_id.length > 0) {
    filters.seller_id.forEach(id => params.append("seller_id[]", id.toString()));
  }
  if (filters?.lottery_id && filters.lottery_id.length > 0) {
    filters.lottery_id.forEach(id => params.append("lottery_id[]", id.toString()));
  }

  return apiAuth.get(`stocks/sellers-summary?${params.toString()}`);
};

export const getStocksSellersPendingDetails = (
  contestId: number
): apiResponseType => {
  return apiAuth.get(`stocks/sellers-pending?contest_id=${contestId}`);
};

export interface StockExportFilters {
  contest_id?: number;
  product_name?: string;
  seller_id?: number[];
  lottery_id?: number[];
  start_date?: string;
  end_date?: string;
  format?: "pdf" | "xlsx";
}

export const exportStocks = (
  filters?: StockExportFilters
): Promise<Blob> => {
  const params = new URLSearchParams();

  if (filters?.contest_id) params.append("contest_id", filters.contest_id.toString());
  if (filters?.product_name) params.append("product_name", filters.product_name);
  if (filters?.seller_id && filters.seller_id.length > 0) {
    filters.seller_id.forEach(id => params.append("seller_id[]", id.toString()));
  }
  if (filters?.lottery_id && filters.lottery_id.length > 0) {
    filters.lottery_id.forEach(id => params.append("lottery_id[]", id.toString()));
  }
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);
  if (filters?.format) params.append("format", filters.format);

  return apiAuth.get(`stocks/export?${params.toString()}`, {
    responseType: "blob",
  }).then((response: any) => response.data);
};

export const exportStocksSellersSummary = (
  filters?: StockExportFilters
): Promise<Blob> => {
  const params = new URLSearchParams();

  if (filters?.seller_id && filters.seller_id.length > 0) {
    filters.seller_id.forEach(id => params.append("seller_id[]", id.toString()));
  }
  if (filters?.lottery_id && filters.lottery_id.length > 0) {
    filters.lottery_id.forEach(id => params.append("lottery_id[]", id.toString()));
  }
  if (filters?.start_date) params.append("start_date", filters.start_date);
  if (filters?.end_date) params.append("end_date", filters.end_date);
  if (filters?.format) params.append("format", filters.format);

  return apiAuth.get(`stocks/sellers-summary/export?${params.toString()}`, {
    responseType: "blob",
  }).then((response: any) => response.data);
};

export interface ContestSummaryProduct {
  product_classification_id: number;
  product_name: string;
  name: string;
  sale_price: number;
  balance: number;
  total_value: number;
  in_progress_sales: Array<{
    sale_id: number;
    sale_uuid: string;
    sale_created_at: string;
    sale_total: number;
    quantity: number;
    product_price_total: number;
    sale_status: { id: number; name: string };
    sale_type: { id: number; name: string };
    lottery: { id: number; name: string };
    client: { id: number; name: string };
    seller: { id: number; name: string } | null;
    unity: { id: number; name: string };
  }>;
  pending_distributions: Array<{
    distribution_id: number;
    quota_quantity: number;
    distribution_created_at: string;
    status: { id: number; name: string };
    distribution_type: { id: number; name: string };
    to_seller: { id: number; name: string } | null;
    to_unity: { id: number; name: string } | null;
    from_seller: { id: number; name: string; unity?: { id: number; name: string } } | null;
    from_unity: { id: number; name: string } | null;
    created_by: string;
    product_price_total: number;
  }>;
}

export interface ContestSummaryResponse {
  contest: {
    uuid: string;
    name: string;
    lottery: string;
  };
  products: ContestSummaryProduct[];
  summary: {
    total_quantity: number;
    total_value: number;
    total_in_progress_quantity: number;
    total_in_progress_value: number;
    total_pending_quantity: number;
    total_pending_value: number;
  };
}

export const getContestSummary = (contestId: number, unityId?: number): apiResponseType => {
  const params = new URLSearchParams({ contest_id: contestId.toString() });
  if (unityId) params.append('unity_id', unityId.toString());
  return apiAuth.get(`stocks/contest-summary?${params.toString()}`);
};

// Olhinho do resumo: bolões numerados de uma linha (faixa) do resumo e onde estão as cotas
export interface ContestSummaryProductSeller {
  seller_id: number;
  seller_name: string;
  balance: number;
}

export interface ContestSummaryProductLocation {
  unity_id: number | null;
  unity_name: string;
  unity_balance: number;
  sellers_balance: number;
  total_balance: number;
  sellers: ContestSummaryProductSeller[];
}

export interface ContestSummaryProductItem {
  cp_classification_id: number;
  product_name: string;
  product_number: number;
  balance: number;
  total_value: number;
  unity_balance: number;
  sellers_balance: number;
  locations: ContestSummaryProductLocation[];
}

export interface ContestSummaryProductsResponse {
  contest: {
    uuid: string;
    name: string;
    lottery: string;
  };
  product_classification: {
    id: number;
    name: string;
    sale_price: number;
  };
  unity_id: number | null;
  products: ContestSummaryProductItem[];
  summary: {
    total_products: number;
    total_balance: number;
    total_value: number;
  };
}

export const getContestSummaryProducts = (
  contestId: number,
  productClassificationId: number,
  unityId?: number
): apiResponseType => {
  const params = new URLSearchParams({
    contest_id: contestId.toString(),
    product_classification_id: productClassificationId.toString(),
  });
  if (unityId) params.append("unity_id", unityId.toString());
  return apiAuth.get(`stocks/contest-summary/products?${params.toString()}`);
};
